// Documentation references:
//
// Postgres Database Schema
// https://baseball.atlassian.net/wiki/spaces/BET/pages/6464077899/Postgres+Database+Schema
//
// Resolved Firestore Configuration Schema
// https://baseball.atlassian.net/wiki/spaces/BET/pages/6465060938/Resolved+Firestore+Configuration+Schema

// GET/read shape for a ProVenue instance. The backend never returns the
// credential secrets (`consumerKey`, `consumerSecret`) — they are write-only —
// so the display type only carries the non-sensitive metadata. If an older
// backend still echoes the secrets, the API normalization layer discards them
// before they ever reach UI state.
export interface ProVenueInstance {
  name: string
  hostName: string
}

// Write shape for a ProVenue instance used in create/update/bulk requests.
// `consumerKey` and `consumerSecret` are only ever supplied by the user; they
// are never populated from an API response. An empty credential string on an
// existing instance means "keep the current value" on the backend.
export interface ProVenueInstanceInput {
  name: string
  hostName: string
  consumerKey: string
  consumerSecret: string
}

export interface AppleVendor {
  keyPath: string
  merchantID: string
  password: string
  vendor: string
}

export interface GoogleVendor {
  collectorId: string
  keyPath: string
  keyVersion: number
  password: string
  vendor: string
}

export interface DisplayConfig {
  client_pk: number | null
  device_pk: number | null
  enum_value: string
}

export const OPERATING_MODE = {
  HANDHELD: 0,
  SUITE: 1,
  TURNSTILE: 2,
  GATE: 3,
  PEDESTAL: 4,
} as const

export type OperatingMode = (typeof OPERATING_MODE)[keyof typeof OPERATING_MODE]

export const OPERATING_MODE_OPTIONS: ReadonlyArray<{ value: OperatingMode; label: string }> = [
  { value: OPERATING_MODE.HANDHELD, label: "Handheld" },
  { value: OPERATING_MODE.SUITE, label: "Suite/Wall Mount" },
  { value: OPERATING_MODE.TURNSTILE, label: "Turnstile" },
  { value: OPERATING_MODE.GATE, label: "Gate" },
  { value: OPERATING_MODE.PEDESTAL, label: "Pedestal" },
]

export const DISPLAY_CONFIGURATION_ITEM = {
  CONNECTIVITY: "ConfigurationConnectivityItem",
  FEATURE_FLAGS: "ConfigurationFeatureFlagsItem",
  OFFLINE_MODE: "ConfigurationOfflineModeItem",
  OPERATING_MODE: "ConfigurationOperatingModeItem",
  SOUND: "ConfigurationSoundItem",
  TICKET_VALIDATION: "ConfigurationTicketValidationItem",
  USER_OPTIONS: "ConfigurationUserOptions",
} as const

export type DisplayConfigurationItem =
  (typeof DISPLAY_CONFIGURATION_ITEM)[keyof typeof DISPLAY_CONFIGURATION_ITEM]

export const DISPLAY_CONFIGURATION_ITEM_OPTIONS: ReadonlyArray<{
  value: DisplayConfigurationItem
  label: string
}> = [
  { value: DISPLAY_CONFIGURATION_ITEM.CONNECTIVITY, label: "Connectivity" },
  { value: DISPLAY_CONFIGURATION_ITEM.FEATURE_FLAGS, label: "Feature Flags" },
  { value: DISPLAY_CONFIGURATION_ITEM.OFFLINE_MODE, label: "Offline Mode" },
  { value: DISPLAY_CONFIGURATION_ITEM.OPERATING_MODE, label: "Operating Mode" },
  { value: DISPLAY_CONFIGURATION_ITEM.SOUND, label: "Sound" },
  { value: DISPLAY_CONFIGURATION_ITEM.TICKET_VALIDATION, label: "Ticket Validation" },
  { value: DISPLAY_CONFIGURATION_ITEM.USER_OPTIONS, label: "User Options" },
]

// AlfredGlobalConfig mirrors the backend struct used for both GET (resolved
// global defaults) and PATCH (partial updates). Sensitive credential fields are
// intentionally absent from the GET response: `configurationPassword` and
// `ticketmasterConsumerKey` are write-only, and `proVenueInstances` only
// carries non-sensitive metadata (see ProVenueInstance). Those values can still
// be supplied in a PATCH to rotate them.
export interface AlfredGlobalConfig {
  validationVendor: string
  proVenueUser: string
  proVenueInstances: ProVenueInstance[]
  facilityCode: string
  league: string
  areaCode: string
  deviceCode: string
  offlineValidationMode: boolean
  offlineBatchUploadLimit: number
  soundValidNumber: number
  soundInvalidNumber: number
  soundWarningNumber: number
  maxCpuTemperature: number
  minCpuTemperature: number
  configurationPassword?: string
  exitScanningAllowed: boolean
  adminModeIdleWindow: number
  validDisplayTime: number
  invalidDisplayTime: number
  operatingMode: OperatingMode
  turnstileMode: number
  unlockedAccessTime: number
  isLockFailSafe: boolean
  useSimpleResponse: boolean
  mainBackgroundColor: string
  secondaryBackgroundColor: string
  idleStateColor: string
  nfcEnabled: boolean
  enableFan: boolean
  enableHotspot: boolean
  wakeLockEnabled: boolean
  enableScanReset: boolean
  enableManualValidationMask: boolean
  displayConfiguration: DisplayConfigurationItem[]
  appleVendors: AppleVendor[]
  googleVendors: GoogleVendor[]
  ticketmasterVenueId: string
}

export type AlfredGlobalConfigKey = keyof AlfredGlobalConfig

// PATCH /api/v1/config/alfred/global accepts a partial of the global config —
// any subset of editable fields may be supplied. Sensitive credential fields
// are write-only, so they are typed here (and only here) rather than on the GET
// response: `ticketmasterConsumerKey` rotates the value when supplied, and
// `proVenueInstances` carries the full credential input shape.
export type UpdateAlfredGlobalConfigRequest = Partial<
  Omit<AlfredGlobalConfig, "proVenueInstances">
> & {
  proVenueInstances?: ProVenueInstanceInput[]
  ticketmasterConsumerKey?: string
}
